import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Loader2, ShieldCheck, Star, Lock } from "lucide-react";
import { toast } from "sonner";

const STATUS_CLS = {
  trialing: "bg-emerald-100 text-emerald-700",
  active: "bg-emerald-100 text-emerald-700",
  past_due: "bg-amber-100 text-amber-700",
  canceled: "bg-red-100 text-red-700",
  expired: "bg-red-100 text-red-700",
  owner: "bg-[#0F172A] text-white",
  none: "bg-slate-200 text-slate-700",
};
const STATUS_KEY = {
  trialing: "trialing", active: "active", past_due: "pastDue", canceled: "canceled",
  expired: "expired", owner: "owner", none: "none",
};

const eur = (n, dec = 2) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

export default function Subscription() {
  const { t, i18n } = useTranslation();
  const { refreshUser } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [billing, setBilling] = useState("month");
  const [commit, setCommit] = useState(false);
  const [commitError, setCommitError] = useState(false);
  const [preselect, setPreselect] = useState("");

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" }); }
    catch { return "—"; }
  };

  const load = () => api.get("/subscription").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); refreshUser?.(); /* eslint-disable-next-line */ }, []);

  // Présélection depuis l'URL (ex. clic sur une formule depuis la page d'accueil : /abonnement?plan=starter&billing=month)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const plan = params.get("plan");
    const b = params.get("billing");
    if (b === "year" || b === "month") setBilling(b);
    if (plan) setPreselect(plan);
  }, [location.search]);

  useEffect(() => {
    if (preselect && data) {
      const el = document.getElementById(`plan-card-${preselect}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [preselect, data]);

  // Retour de paiement Stripe : /abonnement?session_id=... -> synchronise l'abonnement ET génère la facture.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    setBusy("confirming");
    api.get(`/billing/status?session_id=${encodeURIComponent(sid)}`)
      .then(async () => {
        toast.success(t("sub.toast.activated") || "Abonnement activé. Votre facture est disponible dans « Mes factures ».");
        await load(); await refreshUser?.();
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Confirmation du paiement en cours…");
      })
      .finally(() => {
        setBusy("");
        // Nettoie l'URL pour éviter un rejeu au rafraîchissement.
        window.history.replaceState({}, "", "/abonnement");
      });
    // eslint-disable-next-line
  }, [location.search]);

  const subscribe = async (planId) => {
    if (!commit) {
      setCommitError(true);
      toast.error(t("sub.toast.commitRequired"));
      document.getElementById("commit-box")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setBusy(planId);
    try {
      // TOUS les utilisateurs connectés (y compris en essai) peuvent souscrire à tout moment.
      // On appelle TOUJOURS le checkout Stripe (jamais conditionné à stripe_ready, jamais d'endpoint admin) :
      // le backend renvoie une erreur claire si Stripe n'est pas configuré.
      const { data: res } = await api.post("/billing/checkout", { plan: planId, billing, commit_accepted: true, origin_url: window.location.origin });
      if (res?.url) { window.location.href = res.url; return; }
      toast.info(t("sub.toast.paymentSoon"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("sub.toast.actionFailed"));
    } finally {
      setBusy("");
    }
  };

  const cancelSub = async () => {
    if (!window.confirm(t("sub.toast.cancelConfirm"))) return;
    setBusy("cancel");
    try {
      const { data: res } = await api.post("/billing/cancel");
      toast.success(res?.message || t("sub.toast.cancelScheduledOk"));
      await load(); await refreshUser?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("sub.toast.cancelFailed"));
    } finally { setBusy(""); }
  };

  const reactivateSub = async () => {
    setBusy("reactivate");
    try {
      const { data: res } = await api.post("/billing/reactivate");
      toast.success(res?.message || t("sub.toast.reactivatedOk"));
      await load(); await refreshUser?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("sub.toast.reactivateFailed"));
    } finally { setBusy(""); }
  };

  if (!data) return <div className="p-8 text-slate-400" data-testid="subscription-loading">{t("sub.loading")}</div>;

  const sub = data.subscription || {};
  const stCls = STATUS_CLS[sub.status] || STATUS_CLS.none;
  const stLabel = t(`sub.status.${STATUS_KEY[sub.status] || "none"}`);
  const currentPlan = sub.plan;
  const endDate = sub.status === "trialing" ? sub.trial_end : sub.current_period_end;

  return (
    <div className="mx-auto max-w-5xl px-6 py-14 lg:px-10" data-testid="subscription-page">
      <header className="mb-12">
        <p className="overline text-[#C6A87C]">{t("sub.overline")}</p>
        <h1 className="mt-3 font-display text-4xl font-normal tracking-tight text-[#0F172A] sm:text-5xl">{t("sub.title")}</h1>
        <p className="mt-3 max-w-xl font-light text-slate-500">{t("sub.subtitle")}</p>
      </header>

      {preselect && data && (data.plans || []).some((p) => p.id === preselect) && (
        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-[#C6A87C]/50 bg-[#C6A87C]/10 p-4" data-testid="subscription-preselect-banner">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#8a6d3b]" />
          <p className="text-sm text-[#5c4a2c]">
            Finalisez votre abonnement à la formule <b>{((data.plans || []).find((p) => p.id === preselect) || {}).name}</b> : cochez l'engagement de 12 mois ci-dessous, puis cliquez sur « {t("sub.choosePlan")} » pour accéder au paiement sécurisé.
          </p>
        </div>
      )}

      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="subscription-status">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${stCls}`} data-testid="subscription-status-badge">
              <ShieldCheck className="h-3.5 w-3.5" /> {stLabel}
            </span>
            <p className="mt-3 text-sm text-slate-500">
              {sub.status === "owner" ? t("sub.ownerAccess") : (
                <>{t("sub.formula")} : <b className="text-[#0F172A]">{currentPlan ? currentPlan.toUpperCase() : "—"}</b> · {sub.status === "trialing" ? t("sub.endTrial") : t("sub.nextDue")} : <b className="text-[#0F172A]">{fmtDate(endDate)}</b></>
              )}
            </p>
            {sub.status !== "owner" && sub.commitment_end && (
              <p className="mt-1.5 text-xs font-medium text-slate-500" data-testid="subscription-commitment-end">
                {t("sub.commitmentEnd", { date: fmtDate(sub.commitment_end) })}
              </p>
            )}
          </div>
          <div className={`text-sm font-semibold ${data.radar_access ? "text-emerald-600" : "text-red-600"}`} data-testid="subscription-access">
            {data.radar_access ? t("sub.accessActive") : t("sub.accessBlocked")}
          </div>
        </div>
        {(sub.status === "trialing" || sub.status === "active") && sub.stripe_subscription_id && (
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
            {sub.cancel_at_period_end ? (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700" data-testid="subscription-cancel-scheduled">
                  {t("sub.cancelScheduled", { date: fmtDate(sub.cancel_at || sub.commitment_end || endDate) })}
                </span>
                <Button variant="outline" disabled={busy === "reactivate"} onClick={reactivateSub} data-testid="subscription-reactivate-btn" className="h-9 rounded-full">
                  {busy === "reactivate" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sub.reactivateBtn")}
                </Button>
              </>
            ) : (
              <Button variant="outline" disabled={busy === "cancel"} onClick={cancelSub} data-testid="subscription-cancel-btn" className="h-9 rounded-full border-red-200 text-red-700 hover:bg-red-50">
                {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sub.cancelBtn")}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="mt-10 flex flex-col items-center gap-3" data-testid="billing-toggle">
        <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1">
          <button data-testid="billing-monthly" onClick={() => setBilling("month")}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition ${billing === "month" ? "bg-[#0F172A] text-white" : "text-slate-500 hover:text-[#0F172A]"}`}>
            {t("sub.billingMonthly")}
          </button>
          <button data-testid="billing-annual" onClick={() => setBilling("year")}
            className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition ${billing === "year" ? "bg-[#0F172A] text-white" : "text-slate-500 hover:text-[#0F172A]"}`}>
            {t("sub.billingAnnual")} <span className="rounded-full bg-[#C6A87C] px-2 py-0.5 text-[10px] font-bold text-[#0F172A]">{t("sub.annualBadge")}</span>
          </button>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C6A87C]/40 bg-[#C6A87C]/10 px-3.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#8a6d3b]" data-testid="commitment-badge">
          <Lock className="h-3 w-3" /> {t("sub.commitmentBadge")}
        </span>
      </div>

      <div id="commit-box" className={`mx-auto mt-8 max-w-2xl rounded-2xl border p-5 transition-colors ${commitError ? "border-red-400 bg-red-50" : "border-slate-200 bg-white"}`} data-testid="commit-box">
        <label className="flex cursor-pointer items-start gap-3">
          <input type="checkbox" checked={commit} data-testid="commit-checkbox"
            onChange={(e) => { setCommit(e.target.checked); if (e.target.checked) setCommitError(false); }}
            className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-slate-300 accent-[#C6A87C]" />
          <span className="text-sm leading-relaxed text-slate-600">
            {t("sub.commitText")}{" "}
            <a href="/conditions-generales" target="_blank" rel="noreferrer" className="font-semibold text-[#C6A87C] underline">{t("sub.cgLink")}</a>.
          </span>
        </label>
        {commitError && <p className="mt-2 pl-8 text-xs font-semibold text-red-600" data-testid="commit-error">{t("sub.commitError")}</p>}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {(data.plans || []).map((p) => {
          const isCurrent = currentPlan === p.id && sub.status === "active";
          const featured = p.id === "pro";
          const annual = billing === "year";
          const shown = annual ? eur(p.price_annual_eur) : eur(p.price_eur, 0);
          return (
            <div key={p.id} id={`plan-card-${p.id}`} data-testid={`plan-${p.id}`}
                 className={`relative flex flex-col rounded-2xl border bg-white p-8 transition-all duration-300 ${preselect === p.id ? "border-[#C6A87C] ring-2 ring-[#C6A87C] shadow-[0_24px_60px_-28px_rgba(198,168,124,0.7)]" : featured ? "border-[#C6A87C] shadow-[0_24px_60px_-28px_rgba(198,168,124,0.6)] md:-translate-y-2" : "border-slate-200/80 hover:border-slate-300"}`}>
              {featured && (
                <span data-testid="plan-featured-badge" className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-[#0F172A] px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-[#E7D3AE] shadow-sm">
                  <Star className="h-3 w-3 fill-[#E7D3AE] text-[#E7D3AE]" /> {t("sub.featuredBadge")}
                </span>
              )}
              <h3 className="text-xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Manrope', sans-serif" }}>{p.name}</h3>
              <p className="mt-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
                <span className="text-4xl font-bold tabular-nums tracking-tight text-[#0F172A]" style={{ fontVariantNumeric: "lining-nums tabular-nums" }}>{shown}</span>
                <span className="ml-0.5 text-lg font-semibold text-[#0F172A]" style={{ verticalAlign: "0.85em" }}>€</span>
                <span className="ml-1.5 text-sm font-light text-slate-400" style={{ verticalAlign: "0.5em" }}>/ {annual ? t("sub.perYear") : t("sub.perMonth")}</span>
              </p>
              <div className="mt-2 space-y-0.5" data-testid={`plan-${p.id}-pricing-detail`}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8a6d3b]">{t("sub.planCommitment")}</p>
                {annual ? (
                  <>
                    <p className="text-xs text-slate-500">{t("sub.annualInsteadOf1")} <span className="line-through">{eur(p.annual_full_eur)} €</span> {t("sub.annualInsteadOf2")}</p>
                    <p className="text-xs font-semibold text-emerald-600">{t("sub.annualSavings", { save: eur(p.annual_savings_eur) })}</p>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-500">{t("sub.monthlyTotal", { full: eur(p.annual_full_eur) })}</p>
                    <p className="text-xs text-slate-400">{t("sub.monthlyOrAnnual", { annual: eur(p.price_annual_eur), save: eur(p.annual_savings_eur) })}</p>
                  </>
                )}
              </div>
              <div className="my-7 h-px w-full bg-slate-100" />
              <ul className="flex-1 space-y-3.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm font-light leading-relaxed text-slate-600">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#C6A87C]" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`subscribe-${p.id}`}
                disabled={busy === p.id || isCurrent}
                onClick={() => subscribe(p.id)}
                className={`mt-8 h-12 w-full rounded-full text-sm font-medium tracking-wide transition-colors ${featured
                  ? "bg-[#C6A87C] text-[#0F172A] hover:bg-[#b9986a]"
                  : "border border-slate-300 bg-white text-[#0F172A] hover:bg-[#0F172A] hover:text-white"}`}>
                {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? t("sub.currentPlan") : t("sub.choosePlan")}
              </Button>
            </div>
          );
        })}
      </div>
      {data.stripe_ready ? (
        <p className="mt-8 text-center text-xs font-light leading-relaxed tracking-wide text-slate-400" data-testid="subscription-trial-note">
          {t("sub.trialNote")}
        </p>
      ) : (
        <p className="mt-8 text-center text-xs font-light tracking-wide text-amber-600" data-testid="subscription-test-note">
          {t("sub.testNote")}
        </p>
      )}
    </div>
  );
}
